import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { EagleParam } from '../api/types'
import { t } from '../i18n/lingua'
import { Spinner, timeAgo } from './common'
import { StatoBadge } from './EagleStato'
import { etichettaParametro, formatConUnita } from './eagleTesti'
import { Finestra } from './ModificheGitlab'
import { SerieParametro } from './SerieParametro'

/** What a row of the dashboard knows of a parameter: enough to open its window. */
export interface ParametroDaAprire {
  ci_id: string
  ci_name: string
  key: string
  name?: string
  kind?: EagleParam['kind']
  unit?: string
}

/**
 * A parameter in a window, without leaving the page: the state now, the
 * last value and error, and its chart (`SerieParametro`, with the window
 * selector and — for an interface — traffic, packets, errors). From the
 * wall, the open problems and the alarm history, where a click used to send
 * to the CI's page and the wall was lost. «Apri la scheda del CI» stays for
 * who wants the rest.
 */
export function FinestraParametro({ scelto, ore = 24, onChiudi }: {
  scelto: ParametroDaAprire
  ore?: number
  onChiudi: () => void
}) {
  // undefined = loading, null = the parameter is no longer in the catalogue.
  const [param, setParam] = useState<EagleParam | null | undefined>(undefined)
  useEffect(() => {
    let vivo = true
    setParam(undefined)
    void api.ciEagle(scelto.ci_id)
      .then((d) => { if (vivo) setParam((d.params ?? []).find((p) => p.key === scelto.key) ?? null) })
      .catch(() => { if (vivo) setParam(null) })
    return () => { vivo = false }
  }, [scelto.ci_id, scelto.key])
  const minimo = {
    key: scelto.key, unit: scelto.unit ?? '', warn: null, crit: null, direction: 'above' as const,
    name: scelto.name ?? scelto.key, kind: scelto.kind ?? ('unknown' as EagleParam['kind']),
  }
  const p = param ?? minimo
  return (
    <Finestra titolo={`${scelto.ci_name} · ${etichettaParametro(p)}`} onChiudi={onChiudi}>
      {param === undefined ? <Spinner /> : (
        <>
          <p className="fparam__testa">
            {param?.status && <StatoBadge stato={param.status} />}
            {param?.last_value != null && <span className="mono"> {formatConUnita(param.last_value, param.unit)}</span>}
            {param?.status_since && param.status !== 'ok' && (
              <span className="muted"> · {t('da {quando}', { quando: timeAgo(param.status_since) })}</span>
            )}
            {param?.last_error && <span className="eagle-motivo"> {param.last_error}</span>}
            {param === null && <span className="muted">{t('Il parametro non è più monitorato: restano le misure conservate.')}</span>}
          </p>
          <SerieParametro ciId={scelto.ci_id} param={p} oreIniziali={ore} />
          <p className="fparam__piede">
            <Link to={`/cis/${scelto.ci_id}#eagle`} onClick={onChiudi}>{t('Apri la scheda del CI')}</Link>
          </p>
        </>
      )}
    </Finestra>
  )
}
